'use strict'

import moment from 'moment'
import _ from 'lodash'
import plotly from '@/components/common/plotly'
import TimeseriesProxy from '@/proxies/timeseries'

export default {
  components: {
    plotly
  },
  props: {
    observationPoint: {
      type: Object,
      default: () => { return {} }
    },
    datasourceTypes: {
      type: Array,
      default: () => { return ['remote', 'validated'] }
    },
    dates: {
      type: Array,
      default: () => { return [] }
    },
    aggregation: {
      type: String
    },
    showTickRangeOnly: {
      type: Boolean,
      default: false
    },
    hoverformat : {
      type: String,
      default: undefined
    },
    tickformat : {
      type: String,
      default: undefined
    },
    highlightedTimestamp: {
      type: String,
      default: null,
      validator: (value) => {
        return moment.utc(value).isValid()
      }
    },
    showLegends: {
      type: Boolean,
      default: true
    },
    isXaxisVisible: {
      type: Boolean,
      default: true
    },
    margin: {
      type: Object,
      default: () => { return {t: 30, b: 30} }
    },
    fixedRange: {
      type: Boolean,
      default: true
    },
    tickBarRange: {
      type: Object,
      default: undefined
    },
    selectedValue: {
      type: String,
      default: 'avgValue'
    },
    showAxisRangeSelect: {
      type: Boolean,
      default: true
    },
    centerBarEvent: {
      type: Object,
      default: null
    },
    defaultAxisRangeMode: {
      type: String,
      default: 'default'
    }
  },
  data () {
    return  {
      timeseries: [],
      loading: true,
      selectedAxisRangeMode: this.defaultAxisRangeMode
    }
  },
  computed: {
    showGraph () {
      return !this.loading && (!_.isEmpty(this.observationPoint) || !this.loading && this.datasources.length === 0) && this.dataCount > 0
    },
    show () {
      return this.dataCount > 0 || (this.loading && this.dataCount === 0)
    },
    datasources () {
      return this.observationPoint.datasources
    },
    graphData () {
      let toReturn = []
      let toAdd
      this.timeseries.forEach((timeserie) => {
        toAdd = this.convertToGraphData(timeserie)
        if (Array.isArray(toAdd)) toReturn = [...toReturn, ...toAdd]
        else toReturn.push(toAdd)

      })
      return toReturn
    },
    _dateFrom () {
      if (!_.isEmpty(this.dates)) return this.dates[0]
      return this.dateFrom
    },
    _dateTo () {
      if (!_.isEmpty(this.dates)) return this.dates[1]
      return this.dateTo
    },
    _aggregationMode () {
      if (!_.isEmpty(this.aggregation)) return this.aggregation
      return this.aggregationMode
    },
    dataCount () {
      let toReturn = 0
      this.timeseries.map((timeserie) => toReturn += timeserie.data.length)
      return toReturn
    },
    _hoverformat () {
      if (typeof this.hoverformat !== 'undefined') return this.hoverformat
      switch (this._aggregationMode) {
        case 'dd':
          return '%d.%m.%Y'
        case 'mm':
          return '%m.%Y'
        case 'yyyy':
          return '%Y'
        case 'raw':
        case 'hh':
        default:
          return '%d.%m.%Y %H:%M'
      }
    }
  },
  watch: {
    dates () {
      if (this.datasources.length > 0 && !_.isEmpty(this.dates)) this.updateData()
    },
    dateFrom () {
      if (this.datasources.length > 0 && _.isEmpty(this.dates)) this.updateData()
    },
    observationPoint () {
      if (this.datasources.length > 0) this.updateData()
    },
    aggregationMode () {
      if (this.datasources.length > 0) this.updateData()
      if (this._aggregationMode === 'raw' && this.selectedValue !== 'value') this.selectedValue = 'value'
      if (this._aggregationMode !== 'raw' && this.selectedValue === 'value') this.selectedValue = 'avgValue'
    },
    selectedValue () {
      this.$nextTick(() => {
        if (typeof this.$refs.plotly !== 'undefined') {
          this.$refs.plotly.react()
          this.$refs.plotly.relayout()
        }
      })
    }
  },
  mounted () {
    this.loading = true
    this.fetchTimeseries().then(() => this.updateTimeline()).finally(() => this.loading = false)
  },
  methods: {
    handleError (e) {
      console.log(e)
    },
    measuredTypeName (code) {
      if (this.timeseries.length === 0) return ''
      // SD Toutes les sources de données affichées traites du même paramètre
      let parameterType = this.timeseries.find((timeserie) => timeserie.datasource.parameterType.code === code).datasource.parameterType
      return `${parameterType.label[this.$i18n.locale]} [${parameterType.unit.symbol[this.$i18n.locale]}]`
    },
    getAxisRange (paramterTypeCode) {
      let axisRange = null
      let datasources = []
      let min = null
      let max = null

      switch (this.selectedAxisRangeMode) {
        case 'auto':
          axisRange = null
          break
        case 'validated':
          // On prend en compte uniquement les sources de données validées pour calculer les bornes du graph.
          // Si aucune donnnée validée, on laisse le comportement en 'auto'
          datasources = this.datasources.filter((datasource) => datasource.parameterType.code === paramterTypeCode && datasource.datasourceType.code === 'validated')
          if (datasources.length === 0) axisRange = null
          else
            min = null
            max = null
            datasources.forEach((datasource) => {
              if (datasource.stats.minValue < min || min === null) min = datasource.stats.minValue
              if (datasource.stats.maxValue > max || max === null) max = datasource.stats.maxValue
            })
            axisRange = [min, max]
          break
        default:
          // Par défaut, on prend le min et le max de toutes les données du graphs
          datasources = this.datasources.filter((datasource) => datasource.parameterType.code === paramterTypeCode)
          min = null
          max = null
          datasources.forEach((datasource) => {
            if (datasource.stats.minValue < min || min === null) min = datasource.stats.minValue
            if (datasource.stats.maxValue > max || max === null) max = datasource.stats.maxValue
          })
          axisRange = [min, max]
          break
      }

      return axisRange
    },
    getData (datasourceId, dateFrom = this._dateFrom, dateTo = this._dateTo) {
      return new Promise((resolve, reject) => {
        TimeseriesProxy.extract({
          datasourceId,
          dateFrom: dateFrom,
          dateTo: dateTo,
          aggregationMode: this._aggregationMode
        }).then(resolve).catch(reject)
      })
    },
    updateData () {
      this.loading = true
      this.fetchTimeseries().finally(() => this.loading = false)
    },
    onMouseHover (event) {
      this.$emit('mouse-hover', event)
    },
    onMouseOut (event) {
      this.$emit('mouse-out', event)
    },
    getColor (datasource) {
      let toReturn = datasource.datasourceType.webviewConfig.color
      if (typeof toReturn === 'undefined') toReturn = this.color
      return toReturn
    },
    refreshGraph () {
      this.$nextTick(() => {
        if (typeof this.$refs.plotly !== 'undefined') {
          this.$refs.plotly.react()
          this.$refs.plotly.relayout()
        }
      })
    },
  }
}
